import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { useSignOutMutation } from '@/graphql/__generated__/authentication';
import logger from '@/logger';
import { getLanguageFromPath } from '@/utils/utils';

interface User {
	id: string;
	email: string;
	exp: number;
}

interface AuthState {
	token: string | null;
	confirmToken: string | null;
	resetToken: string | null;
	user: User | null;
	intervalId: NodeJS.Timeout | null;
	setToken: (token: string | null) => void;
	logout: () => void;
	isAuthenticated: () => boolean;
	getAuthHeader: () => string;
	checkTokenExpiration: () => boolean;
	startTokenChecker: () => void;
	stopTokenChecker: () => void;
	initialize: () => void;
}

const useAuthStore = create<AuthState>()(
	persist(
		(set, get) => ({
			token: null,
			user: null,
			confirmToken: null,
			resetToken: null,
			intervalId: null,

			setToken: (token: string | null) => {
				if (token) {
					try {
						const user = jwtDecode<User>(token);
						if (user.exp <= Date.now() / 1000) {
							logger.info('Attempting to set an expired token');
							set({ token: null, user: null });
							return;
						}
						set({ token, user });
						get().startTokenChecker();
					} catch (error) {
						logger.error('Error decoding token:', error);
						set({ token: null, user: null });
					}
				} else {
					get().stopTokenChecker();
					set({ token: null, user: null });
				}
			},

			logout: () => {
				get().stopTokenChecker();
				set({ token: null, user: null });
				const language = getLanguageFromPath(window.location.pathname);
				window.location.href = `/${language}/signin`;
			},

			isAuthenticated: () => {
				const token = get().token;
				if (!token) return false;
				try {
					const decoded = jwtDecode<User>(token);
					return decoded.exp > Date.now() / 1000;
				} catch {
					return false;
				}
			},

			getAuthHeader: () => {
				const token = get().token;
				return token ? `Bearer ${token}` : '';
			},

			checkTokenExpiration: () => {
				const { token, user } = get();

				if (!token || !user) return false;

				const currentTime = Date.now() / 1000;

				if (user.exp <= currentTime) {
					logger.info('Token expired, logging out user');
					get().logout();
					return true;
				}

				return false;
			},

			startTokenChecker: () => {
				get().stopTokenChecker();

				if (get().checkTokenExpiration()) {
					return;
				}

				const intervalId = setInterval(() => {
					get().checkTokenExpiration();
				}, 60000);

				set({ intervalId });
			},

			stopTokenChecker: () => {
				const { intervalId } = get();
				if (intervalId) {
					clearInterval(intervalId);
					set({ intervalId: null });
				}
			},

			initialize: () => {
				const { token, user } = get();

				if (token && user) {
					if (user.exp <= Date.now() / 1000) {
						logger.info('Found expired token on initialization, logging out');
						get().logout();
					} else {
						get().startTokenChecker();
					}
				}
			}
		}),
		{
			name: 'token',
			storage: {
				getItem: (name: string) => {
					const str = sessionStorage.getItem(name);
					return str ? JSON.parse(str) : null;
				},
				setItem: (name: string, value: unknown) => {
					sessionStorage.setItem(name, JSON.stringify(value));
				},
				removeItem: (name: string) => sessionStorage.removeItem(name)
			},
			// Only persist essential data, not the intervalId
			partialize: (state) => ({
				token: state.token,
				user: state.user,
				confirmToken: state.confirmToken,
				resetToken: state.resetToken
			})
		}
	)
);

// Initialize the store when the module loads
if (typeof window !== 'undefined') {
	// Initialize after a short delay to ensure sessionStorage is available
	setTimeout(() => {
		useAuthStore.getState().initialize();
	}, 0);
}

// Custom hook to protect routes
export const useRequireAuth = () => {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const navigate = useNavigate();

	useEffect(() => {
		if (!isAuthenticated()) {
			navigate('/login', { replace: true });
		}
	}, [isAuthenticated, navigate]);
};

// Enhanced useAuth hook with signOut mutation
export const isKogniStudioUser = (email: string | undefined): boolean => {
	if (!email) return false;
	return email.toLowerCase().endsWith('@kogni.studio');
};

export const useAuth = () => {
	const authenticationClient = useApolloClient(SCHEMAS.AUTHENTICATION);
	const [signOutMutation] = useSignOutMutation({
		client: authenticationClient
	});

	const token = useAuthStore((state) => state.token);
	const user = useAuthStore((state) => state.user);
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const storeLogout = useAuthStore((state) => state.logout);
	const setToken = useAuthStore((state) => state.setToken);
	const getAuthHeader = useAuthStore((state) => state.getAuthHeader);

	const logout = async () => {
		try {
			await signOutMutation();
		} catch (error) {
			logger.error('Error during sign out:', error);
		} finally {
			storeLogout();
		}
	};

	return {
		token,
		user,
		isAuthenticated,
		logout,
		setToken,
		getAuthHeader
	};
};

export default useAuthStore;
